import * as Sentry from '@sentry/react';
import React, { Suspense, useEffect } from 'react';
import { Provider } from 'react-redux';
import {
  createRoutesFromChildren, matchRoutes, useLocation, useNavigationType,
  // BrowserRouter,
  RouterProvider,
} from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { v4 as uuidv4 } from 'uuid';

import { router } from '@app/AppRouter';
import appConfig from '@config/app';
import { stringEscape } from '@shared/helpers';
import { LoadingBlock } from '@shared/utils';
import CacheBuster from '@shared/utils/CacheBuster';
import startVersionWatcher from '@shared/utils/CacheBuster/versionWatcher';

import { AppProvider } from './AppContext';
import { store, persistor } from './store';

import './App.scss';

const urlPropagate = new RegExp(`^${stringEscape(appConfig.apiUrl)}`);

function shouldIgnoreError(event: any) {
  // Check if the event has an HTTP status code we want to ignore
  if (event && event.extra && event.extra.status) {
    const { status } = event.extra;
    if ([ 400, 401, 422 ].includes(status)) {
      return true;
    }
  }

  // Optionally, check in the error message or tags if your errors are structured differently
  if (event && event.message) {
    if (
      event.message.includes('401')
      || event.message.includes('400')
      || event.message.includes('422')
    ) {
      return true;
    }
  }

  return false;
}

if (appConfig.sentry.env !== 'local') {
  Sentry.init({
    dsn: appConfig.sentry.dsn,
    environment: appConfig.sentry.env,
    integrations: [
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [ 'localhost', urlPropagate ],
    // Session Replay
    // This sets the sample rate at 10%. You may want to change it to
    // 100% while in development and then sample at a lower rate in production.
    replaysSessionSampleRate: 0.1,
    // If you're not already sampling the entire session, change the sample rate
    // to 100% when sampling sessions where errors occur.
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      if (shouldIgnoreError(event)) {
        // Drop the event, do not send to Sentry
        return null;
      }
      return event;
    },

  });
}

// const LazyAppRouter = lazy(() => import('@app/AppRouter'));
function App() {
  useEffect(() => {
    const deviceUuid = localStorage.getItem('DEVICE_UUID');

    if (!deviceUuid) {
      try {
        localStorage.setItem('DEVICE_UUID', uuidv4());
      } catch (error) {
        //
      }
    }
  }, []);

  // A tab left open across a deploy moves to the new build on its next page change.
  useEffect(() => startVersionWatcher(router), []);

  return (
    <Sentry.ErrorBoundary>
      <CacheBuster>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <Suspense fallback={<LoadingBlock />}>
              <AppProvider>
                {/* <BrowserRouter>
                  <LazyAppRouter />
                </BrowserRouter> */}
                <RouterProvider router={router} future={{ v7_startTransition: true }} />
              </AppProvider>
            </Suspense>
          </PersistGate>
        </Provider>
      </CacheBuster>
    </Sentry.ErrorBoundary>
  );
}

export default App;
